import { Box, TableCell, TableRow } from '@mui/material';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, SwipeComponent, Text } from '@/components/ui';
import { deleteIcon } from '@/assets';
import { EditableCell } from '../components/EditableCell';
import type { QuantityAndPrice } from '../index';
import type { TableStyles } from '../types/styles';

interface MaterialRowProps {
  item: QuantityAndPrice;
  index: number;
  showDeleteElemId: number | null;
  loading: boolean;
  styles: TableStyles;
  onSwiped: (id: number) => void;
  onDeleteMaterial: (id: number) => void;
  onChangeQuantity: (value: number, id: number) => void;
}

export const MaterialRow = memo<MaterialRowProps>(
  ({
    item,
    index,
    showDeleteElemId,
    loading,
    styles,
    onSwiped,
    onDeleteMaterial,
    onChangeQuantity,
  }) => {
    const { t } = useTranslation();
    const isEven = index % 2 === 0;

    const deleteSlideBoxStyle = {
      height: '100%',
      position: 'absolute' as const,
      background: 'red',
      top: 0,
      display: 'flex',
      alignItems: 'center',
      padding: 1,
      left: 0,
      '&.slide-in': {
        transform: 'translateX(0)',
        transition: 'transform 0.3s ease-in-out',
      },
      '&.slide-out': {
        transform: 'translateX(-150%)',
        transition: 'transform 0.3s ease-in-out',
      },
    };

    return (
      <TableRow sx={isEven ? styles.evenRow : styles.oddRow}>
        <TableCell
          onClick={() => onSwiped(item.id)}
          sx={{ ...styles.codeCell, paddingLeft: 0.5 }}
        >
          <Box
            className={showDeleteElemId === item.id ? 'slide-in' : 'slide-out'}
            sx={deleteSlideBoxStyle}
            onClick={() => onDeleteMaterial(item.id)}
          >
            <Box sx={{ height: 18, mx: 0.5 }}>
              <Icon src={deleteIcon} />
            </Box>
            <Text sx={{ color: 'white' }}>{t('delete_material_table')}</Text>
          </Box>
          <SwipeComponent swiped={() => onSwiped(item.id)}>
            <Text sx={{ ...styles.cellText, paddingLeft: 0.5 }}>
              {item.materialCode || 'N/A'}
            </Text>
          </SwipeComponent>
        </TableCell>

        <TableCell
          onClick={() => onSwiped(item.id)}
          sx={styles.descriptionCell}
        >
          <SwipeComponent swiped={() => onSwiped(item.id)}>
            <Text sx={styles.cellText}>{item.shortDescription || 'N/A'}</Text>
          </SwipeComponent>
        </TableCell>

        <TableCell sx={styles.priceCell}>
          <Text sx={styles.cellText}>₪{item.unitCost.toFixed(2)}</Text>
        </TableCell>

        <TableCell onClick={() => onSwiped(item.id)} sx={styles.unitCell}>
          <SwipeComponent swiped={() => onSwiped(item.id)}>
            <Text sx={styles.cellText}>{item.unitType || 'N/A'}</Text>
          </SwipeComponent>
        </TableCell>

        <TableCell sx={{ ...styles.quantityCell, paddingLeft: 0.5 }}>
          <EditableCell
            value={item.quantity}
            loading={loading}
            onChange={value => onChangeQuantity(value, item.materialId)}
          />
        </TableCell>

        <TableCell sx={styles.totalCell}>
          <Text sx={{ ...styles.cellText, fontWeight: 'bold' }}>
            ₪{(item.quantity * item.unitCost).toFixed(2)}
          </Text>
        </TableCell>
      </TableRow>
    );
  }
);
